import { useState } from "react";
import {
  UPLOAD_PRESETS,
  validateUploadFile,
} from "../utils/validateUploadFile";
import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

const MAX_DESCRICAO_CHARS = 425;

/**
 * Componente para upload de itens/documentos do checklist
 * @param {Array} itensChecklist - Lista de itens com estrutura: {id, descricao, observacoes, obrigatorio}
 * @param {Object} documentosErros - Objeto contendo erros de validação dos documentos
 * @param {Object} documentos - Objeto contendo arquivos já carregados
 * @param {Object} documentosChecklistFiles - Objeto contendo arquivos do checklist carregados
 * @param {Function} onFileChange - Callback ao mudar arquivo (recebe: itemId, arquivo)
 */

function ChecklistItemCard({
  item,
  documentos,
  documentosChecklistFiles,
  documentosErros,
  onFileChange,
}) {
  const theme = useTheme();
  const [expandido, setExpandido] = useState(false);
  const [erroValidacao, setErroValidacao] = useState("");

  const fieldKey = `checklist_doc_${item.id}`;
  const temArquivo =
    documentos[fieldKey] || documentosChecklistFiles[fieldKey];
  const temErro = documentosErros[fieldKey] || erroValidacao;

  const observacoes = item.observacoes || "Sem observações";
  const precisaTruncar = observacoes.length > MAX_DESCRICAO_CHARS;
  const textoExibido =
    precisaTruncar && !expandido
      ? observacoes.slice(0, MAX_DESCRICAO_CHARS)
      : observacoes;

  const handleFileChange = async (event) => {
    const arquivo = event.target.files?.[0];
    if (!arquivo) return;

    event.target.value = "";

    const resultado = await validateUploadFile(arquivo, UPLOAD_PRESETS.documento);
    if (!resultado.valid) {
      setErroValidacao(resultado.error);
      return;
    }

    setErroValidacao("");
    if (onFileChange) {
      onFileChange(item.id, arquivo);
    }
  };

  return (
    <Grid item xs={12} md={4} sx={{ display: "flex" }}>
      <FormControl
        fullWidth
        error={Boolean(temErro)}
        sx={{
          height: "100%",
          border: temArquivo
            ? `1px solid ${theme.palette.success.main}`
            : temErro
            ? `1px solid ${theme.palette.error.main}`
            : `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 2,
          padding: 2,
          position: "relative",
          transition: "all 0.3s",
          backgroundColor: temArquivo
            ? alpha(theme.palette.success.main, 0.05)
            : temErro
            ? alpha(theme.palette.error.main, 0.05)
            : "transparent",
          "&:hover": {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 1px ${alpha(
              theme.palette.primary.main,
              0.3
            )}`,
          },
        }}
      >
        <FormLabel
          sx={{
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            fontWeight: "medium",
          }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              bgcolor: item.obrigatorio
                ? alpha(theme.palette.error.main, 0.1)
                : alpha(theme.palette.primary.main, 0.1),
              borderRadius: "50%",
              mr: 1,
            }}
          >
            <Box component="span" sx={{ fontSize: "0.8rem" }}>
              📄
            </Box>
          </Box>
          <span style={{ flex: 1 }}>{item.descricao}</span>
          {item.obrigatorio && (
            <span style={{ color: "red", marginLeft: "4px" }}> *</span>
          )}
        </FormLabel>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            mb: 1.5,
            p: 1,
            bgcolor: alpha(theme.palette.info.main, 0.05),
            borderRadius: 1,
            borderLeft: `3px solid ${theme.palette.info.main}`,
          }}
        >
          {textoExibido}
          {precisaTruncar && (
            <>
              {!expandido && "... "}
              <Box
                component="span"
                onClick={() => setExpandido((v) => !v)}
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  fontWeight: "medium",
                  ml: 0.25,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {expandido ? "Ver menos" : "Ver mais"}
              </Box>
            </>
          )}
        </Typography>

        <Box
          sx={{
            position: "relative",
            "& .MuiInput-root": {
              bgcolor: "#f8f9fa",
              border: `1px dashed ${alpha(
                theme.palette.text.primary,
                0.2
              )}`,
              borderRadius: 1,
              p: 1,
              "&:hover": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        >
          <Input
            type="file"
            required={item.obrigatorio}
            onChange={(e) => handleFileChange(e)}
            inputProps={{
              accept: ".pdf,.png,.jpg,.jpeg",
              "data-testid": `documento-checklist-${item.id}`,
            }}
            sx={{
              "&::before, &::after": {
                display: "none",
              },
            }}
          />
        </Box>

        {temErro && (
          <Typography
            color="error"
            variant="caption"
            sx={{ display: "block", mt: 1 }}
          >
            {temErro}
          </Typography>
        )}

        {temArquivo && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 1.5,
              p: 1,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.success.main, 0.1),
            }}
          >
            <Box
              component="span"
              sx={{
                display: "flex",
                mr: 1,
                color: theme.palette.success.main,
                fontSize: "1.2rem",
              }}
            >
              ✓
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.success.dark,
                fontWeight: "medium",
                flexGrow: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {temArquivo.name}
            </Typography>
          </Box>
        )}
      </FormControl>
    </Grid>
  );
}

function ChecklistDocumentosUpload({
  itensChecklist,
  documentosErros,
  documentos,
  documentosChecklistFiles,
  onFileChange,
}) {
  if (!itensChecklist || itensChecklist.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhum documento necessário para esta atividade.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {itensChecklist.map((item) => (
        <ChecklistItemCard
          key={item.id}
          item={item}
          documentos={documentos}
          documentosChecklistFiles={documentosChecklistFiles}
          documentosErros={documentosErros}
          onFileChange={onFileChange}
        />
      ))}
    </Grid>
  );
}

export default ChecklistDocumentosUpload;
